<template>
  <v-card class="orange lighten-5" elevation="3">
    <v-card-title class="white--text orange">Fullfør bestilling</v-card-title>

    <v-form ref="form">
      <v-container>
        <v-row>
          <v-col cols="12">
            <CheckoutDeliveryMethods
              ref="delivery-methods"
              :vendor="vendor"
              :order="order"
              :delivery-methods="delivery_methods"
              @update:deliveryMethod="updateDeliveryMethod($event)"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <CheckoutPaymentMethods
              ref="payment-methods"
              :vendor="vendor"
              :order="order"
              @update:stripeToken="updateStripeToken($event)"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" lg="4">
            <v-text-field
              v-model="name"
              label="Navn"
              :rules="[rules.nameRules]"
              required
            />
          </v-col>

          <v-col cols="12" lg="4">
            <v-text-field
              v-model="phone"
              label="Mobilnummer"
              :rules="[rules.phoneRules]"
              required
            />
          </v-col>

          <v-col cols="12" lg="4">
            <v-text-field
              v-model="email"
              label="E-post"
              :rules="[rules.emailRulesEmpty]"
              required
            />
          </v-col>
        </v-row>

        <v-row v-if="showDeliveryAddress()">
          <v-col cols="12" lg="4">
            <v-text-field v-model="delivery_address" label="Adresse" />
          </v-col>

          <v-col cols="12" lg="4">
            <v-text-field v-model="delivery_zip" label="Postnr" />
          </v-col>

          <v-col cols="12" lg="4">
            <v-text-field v-model="delivery_region" label="Poststed" />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <i
              >Ved å fullføre en bestilling godtar du butikken sine
              <a href="#" @click.prevent="showTerms = true">vilkår</a>.</i
            >
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-btn
              :disabled="!isFormValid || isLoading"
              :loading="isLoading"
              color="success"
              @click="placeOrderSubmit"
            >
              <v-icon left> mdi-cart-arrow-right </v-icon>
              Fullfør bestilling
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-form>

    <v-dialog
      v-model="showTerms"
      transition="dialog-top-transition"
      max-width="600"
      scrollable
    >
      <template v-slot:default="dialog">
        <v-card>
          <v-toolbar color="primary" dark
            >Vilkår for {{ organization.name }}</v-toolbar
          >
          <!-- eslint-disable vue/no-v-html -->
          <v-card-text class="mt-3" v-html="organization.terms_rendered" />
          <!--eslint-enable-->

          <v-card-actions class="justify-end">
            <v-btn text @click="dialog.value = false">Lukk vilkår</v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
  </v-card>
</template>

<script>
import CheckoutPaymentMethods from "@/components/CheckoutPaymentMethods";
import CheckoutDeliveryMethods from "@/components/CheckoutDeliveryMethods";
import backend from "@/backend";

export default {
  name: "Checkout",

  components: {
    CheckoutPaymentMethods,
    CheckoutDeliveryMethods,
  },

  props: {
    order: {
      type: Object,
      required: true,
    },
    vendor: {
      type: Object,
      required: true,
    },
    organization: {
      type: Object,
      required: true,
    },
  },

  data: () => ({
    showTerms: false,
    name: "",
    phone: "",
    email: "",
    delivery_address: "",
    delivery_zip: "",
    delivery_region: "",
    delivery_methods: [],
    stripeToken: null,

    isLoading: false,

    rules: {
      nameRules: function (val) {
        if (!val) {
          return "Vennligst fyll inn et navn.";
        }

        return true;
      },

      emailRulesEmpty: (v) => !!v || "Vennligst fyll inn en e-post adresse.",

      emailRulesValid: (v) =>
        /.+@.+/.test(v) || "E-post adressen er ikke gyldig.",

      phoneRules: function (val) {
        if (val.length != 8) {
          return "Mobilnummer må være 8 siffer.";
        }
        return true;
      },
    },
  }),

  computed: {
    isFormValid() {
      return (
        this.name != "" &&
        this.email != "" &&
        this.phone != "" &&
        this.order.payment != "" &&
        this.order.delivery_method != null &&
        this.total > 0 &&
        this.paymentValid() &&
        this.deliveryAddressValid()
      );
    },

    total() {
      return this.order.orderlines.reduce((total, orderline) => {
        return total + orderline.product.price * orderline.quantity;
      }, 0);
    },
  },

  async created() {
    this.$on("checkout-place-order", this.placeOrder);
    this.delivery_methods = await backend.get(`deliverymethods/`, {
      organization: this.vendor.organization,
      vendor: this.vendor.id,
    });
  },

  methods: {
    showDeliveryAddress() {
      return (
        this.order.delivery_method !== null &&
        this.order.delivery_method.requires_delivery_address
      );
    },

    updateDeliveryMethod(method) {
      this.order.delivery_method = method;
    },

    updateStripeToken(token) {
      this.stripeToken = token;
      // Since we got a Stripe token, lets fire off the
      // rest of the place order mechanism.
      this.$emit("checkout-place-order");
    },

    deliveryAddressValid() {
      if (!this.order.delivery_method.requires_delivery_address) {
        return true;
      }

      return (
        this.delivery_street != "" &&
        this.delivery_zip != "" &&
        this.delivery_region != ""
      );
    },
    paymentValid() {
      // Non-stripe is always valid (no input)
      if (this.order.payment != "stripe") {
        return true;
      }

      return this.$refs["payment-methods"].stripeCardValid;
    },

    placeOrderSubmit() {
      this.isLoading = true;

      if (this.order.payment == "stripe") {
        // Stripe requires some work before we can place the order,
        // we need to create a token to pass along to the payment.
        this.$refs["payment-methods"].$refs["stripe-element-card"].submit();
      } else {
        this.$emit("checkout-place-order");
      }
    },
    async placeOrder() {
      if (this.order.payment == "stripe" && this.stripeToken === null) {
        this.isLoading = false;
        throw "Payment method is Stripe but no token yet, can't proceed";
      }

      const orderData = await backend.post(
        "orders/",
        {
          vendor: this.vendor.id,
          name: this.name,
          email: this.email,
          phone: this.phone,
          payment: this.order.payment,
          delivery_method: this.order.delivery_method.id,
          delivery_address: this.delivery_address,
          delivery_zip: this.delivery_zip,
          delivery_region: this.delivery_region,
        },
        {
          vendor: this.vendor.id,
        }
      );

      for (const ol of this.order.orderlines) {
        await backend.post(
          "orderlines/",
          {
            order: orderData.id,
            product: ol.product.id,
            quantity: ol.quantity,
          },
          {
            order: orderData.id,
          }
        );
      }

      const stripeToken =
        this.stripeToken === null ? null : this.stripeToken.id;
      const paymentData = await backend.post(
        "payments/",
        {
          order: orderData.id,
          return_origin: document.location.origin,
          stripe_token: stripeToken,
        },
        {
          order: orderData.id,
        }
      );

      this.$router.push({
        name: "paymentLoading",
        params: {
          paymentId: paymentData.id.toString(),
        },
      });
    },
  },
};
</script>
